import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CollectionLetterSummary,
  getCollectionLetterSummary,
  queryKeys,
  reportError,
} from '@vgrubs/components';
import { useEffect } from 'react';

/** Section title, then its `- bullet` lines, sections a blank line apart. */
export const summaryToText = (summary: CollectionLetterSummary) =>
  summary.sections
    .map(({ title, bullets }) =>
      [title, ...bullets.map((bullet) => `- ${bullet}`)].join('\n')
    )
    .join('\n\n');

/**
 * The call summary of a deleted location that owes, from the click until its
 * modal closes. The button spins while it loads and the modal only opens
 * once it is here.
 *
 * Never kept once the modal closes: every open asks again, as
 * the amounts follow the letter generated that day and move with each
 * payment. A failed request lands the backend's message in the error toast
 * and calls `onError`, which drops the request so the modal never opens.
 */
export const useCollectionLetterSummary = (
  locationId: number,
  isRequested: boolean,
  onError: () => void
) => {
  const queryClient = useQueryClient();
  const queryKey =
    queryKeys.admin.receivables.collectionLetterSummary(locationId);
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const { data } = await getCollectionLetterSummary(locationId);
      return data.data;
    },
    enabled: isRequested && Number.isFinite(locationId),
    staleTime: 0,
    // A 400 will not fix itself, and a slow one already took up to a minute.
    retry: false,
  });

  const { error } = query;
  useEffect(() => {
    if (!error) return;
    reportError(error, { context: 'collectionLetterSummary' });
    onError();
    // onError is a fresh closure every render; the error alone is the trigger.
  }, [error]);

  // The page stays subscribed, so `gcTime` alone would keep the old answer
  // and the next click would open on it. Dropped on close: the next click
  // starts from nothing and the button spins again.
  useEffect(() => {
    if (!isRequested) queryClient.removeQueries({ queryKey, exact: true });
    // queryKey is a fresh array every render; locationId is what it is made of.
    // The client never changes.
  }, [isRequested, locationId]);

  return {
    summary: isRequested ? query.data : undefined,
    isLoading: isRequested && query.isLoading,
  };
};
